import { useMemo } from 'react'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { Pressable } from './Pressable'

export type DateFlipperGranularity = 'day' | 'week' | 'month'

type Props = {
  value: Date
  granularity: DateFlipperGranularity
  onChange: (next: Date) => void
  onGranularityChange?: (next: DateFlipperGranularity) => void
  granularities?: DateFlipperGranularity[]
  className?: string
  label?: string
}

const shiftDate = (date: Date, granularity: DateFlipperGranularity, direction: 1 | -1): Date => {
  const clone = new Date(date)
  if (granularity === 'day') clone.setDate(clone.getDate() + direction)
  else if (granularity === 'week') clone.setDate(clone.getDate() + 7 * direction)
  else clone.setMonth(clone.getMonth() + direction)
  return clone
}

const weekOf = (date: Date): { start: Date; end: Date } => {
  const start = new Date(date)
  start.setDate(start.getDate() - start.getDay())
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return { start, end }
}

/**
 * DateFlipper — segmented prev / today / next with granularity switcher.
 * Zero-dependency, uses only common UI patterns already in the app.
 */
export const DateFlipper = ({
  value,
  granularity,
  onChange,
  onGranularityChange,
  granularities = ['day', 'week', 'month'],
  className,
  label,
}: Props) => {
  const dateLabel = useMemo(() => {
    if (granularity === 'day') {
      return value.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    }
    if (granularity === 'week') {
      const { start, end } = weekOf(value)
      return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
    }
    return value.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }, [value, granularity])

  return (
    <div className={['dateFlipper', className ?? ''].filter(Boolean).join(' ')} aria-label={label ?? 'Date navigation'}>
      {onGranularityChange && granularities.length > 1 && (
        <div className="dateFlipper__gran" role="tablist" aria-label="Granularity">
          {granularities.map((entry) => (
            <Pressable
              key={entry}
              className={`dateFlipper__granBtn${granularity === entry ? ' is-active' : ''}`}
              role="tab"
              aria-selected={granularity === entry}
              onClick={() => onGranularityChange(entry)}
            >
              {entry.charAt(0).toUpperCase() + entry.slice(1)}
            </Pressable>
          ))}
        </div>
      )}
      <div className="dateFlipper__nav">
        <IconButton
          size="sm"
          variant="tertiary"
          className="dateFlipper__arrow"
          onClick={() => onChange(shiftDate(value, granularity, -1))}
          label="Previous"
          icon={<ArrowBackIosNewRoundedIcon fontSize="inherit" />}
        />
        <Button
          size="sm"
          variant="tertiary"
          className="dateFlipper__today"
          icon={<TodayRoundedIcon fontSize="inherit" />}
          onClick={() => onChange(new Date())}
        >
          {dateLabel}
        </Button>
        <IconButton
          size="sm"
          variant="tertiary"
          className="dateFlipper__arrow"
          onClick={() => onChange(shiftDate(value, granularity, 1))}
          label="Next"
          icon={<ArrowForwardIosRoundedIcon fontSize="inherit" />}
        />
      </div>
    </div>
  )
}
